import React, { Fragment, useState, useEffect } from "react";
import {
  Button,
  Divider,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Skeleton,
  TextareaAutosize,
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import { StyledReasonWrapper } from "./styled";
import { AnimatePresence, AnimateSharedLayout, motion } from "framer-motion";
import Quantity from "./quantity";
import ImageUpload from "./ImageUpload";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SameProduct from "./SameProduct";
import DifferentProduct from "./DifferentProduct";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { getReasons, getS3SignedUrls } from "utils/api";
import { getApiErrorMessage } from "utils/apiError";
import { useDispatch, useSelector } from "react-redux";
import { getMerchantConfig, getRmsConfig } from "features/config/configSlice";
import {
  addProceedBool,
  addProductType,
  addSelectedReason,
  addSelectedSubReason,
  addUploadMediaIds,
  getPreviews,
  getProceedBool,
  getProductid,
  getProductType,
  getSelectedGroupProducts,
  getSelectedProduct,
  getSelectedReason,
  getSelectedSubReason,
  getUploads,
  getUploadMediaIds,
  getReplacementMode,
  mobSelected,
  setImageUploads,
  addUploads,
  setReplacementMode,
} from "features/product/productSlice";
import {
  addReturnedProducts,
  getReturnedProducts,
} from "features/returnedProducts/returnedProductSlice";
import { ReactComponent as CloseIcon } from "assets/close.svg";
import { BeatLoader } from "react-spinners";
import Compressor from "compressorjs";
import {
  getSelectedOrder,
  updateWebSelected,
} from "features/orders/orderSlice";
import { toast } from "react-toastify";
import { sendWebhook } from "utils/axiosInstance";
import { LazyLoadImage } from "react-lazy-load-image-component";
import { getLineItemMetadata } from "utils/lineItemMetadata";
import {
  areReturnMediaRequirementsMet,
  buildReturnMediaPresignFiles,
  getReasonMinimumConfiguredMediaUploads,
  getReasonMinimumMediaUploads,
  hasReturnMediaReasonIdentity,
  reasonRequiresMedia,
} from "utils/returnMedia";
import LoadingError from "components/v1/common/LoadingError";

const shuffleArray = (items = []) => {
  const shuffledItems = [...items];
  for (let i = shuffledItems.length - 1; i > 0; i--) {
    const randomIndex = Math.floor(Math.random() * (i + 1));
    [shuffledItems[i], shuffledItems[randomIndex]] = [
      shuffledItems[randomIndex],
      shuffledItems[i],
    ];
  }
  return shuffledItems;
};

const sanitizeUploadFilename = (filename) => {
  const rawName = String(filename || "")
    .replace(/\\/g, "/")
    .split("/")
    .pop()
    .trim();
  const normalizedName = rawName
    .normalize("NFKD")
    .split("")
    .filter((char) => char.charCodeAt(0) <= 127)
    .join("");
  const safeName = normalizedName
    .replace(/[^A-Za-z0-9._-]+/g, "_")
    .replace(/^[._-]+|[._-]+$/g, "");

  if (!safeName) {
    return `upload_${Date.now()}`;
  }

  const extensionIndex = safeName.lastIndexOf(".");
  const hasExtension = extensionIndex > 0;
  const stem = (hasExtension ? safeName.slice(0, extensionIndex) : safeName)
    .slice(0, 100)
    .replace(/^[._-]+|[._-]+$/g, "") || "upload";
  const extension = hasExtension
    ? safeName
        .slice(extensionIndex)
        .toLowerCase()
        .replace(/[^A-Za-z0-9.]/g, "")
        .slice(0, 16)
    : "";

  return `${stem}${extension}`;
};

const Reasonbox = ({
  reason,
  merchantConfig,
  selectedReason,
  handleReason,
  handleSubReason,
}) => {
  const [openBox, setOpenbox] = useState(false);
  const Icon = () => {
    return openBox ? (
      <KeyboardArrowUpIcon
        style={{ color: "#3B444D", fontSize: "18px", display: "block" }}
      />
    ) : (
      <KeyboardArrowDownIcon
        style={{ color: "#3B444D", fontSize: "18px", display: "block" }}
      />
    );
  };

  return (
    <AnimateSharedLayout>
      <motion.div
        layout
        style={{
          border: openBox
            ? `1px solid ${merchantConfig.primary_theme_color}`
            : "1px solid #3B444D1A",
          borderRadius: merchantConfig.use_rounded_edge_buttons ? "6px" : "0px",
          marginTop: "20px",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "10px",
            cursor: "pointer",
          }}
          onClick={() => {
            handleReason(reason, openBox, setOpenbox);
          }}
        >
          <motion.p layout style={{ color: "#3B444D" }}>
            {reason.reason_label}
          </motion.p>
          {reason.sub_reasons.length > 0 && (
            <motion.div layout>
              <Icon />
            </motion.div>
          )}
        </div>
        <AnimatePresence>
          {openBox && reason.sub_reasons.length > 0 && (
            <div>
              {reason.sub_reasons.map((subreason, i) => {
                return (
                  <motion.div
                    key={i}
                    layout
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      padding: "10px",
                      border:
                        selectedReason !== null &&
                        selectedReason.reason_label === subreason.reason_label
                          ? "1px solid #1d8dcc"
                          : "1px solid #3B444D1A",
                      borderRadius: merchantConfig.use_rounded_edge_buttons
                        ? "6px"
                        : "0px",
                      margin: "0px 10px 15px 10px",
                      cursor: "pointer",
                      color: "#3B444D",
                      fontSize: "14px",
                    }}
                    onClick={() => {
                      handleSubReason(reason, subreason);
                    }}
                  >
                    <p>{subreason.reason_label}</p>
                  </motion.div>
                );
              })}
            </div>
          )}
        </AnimatePresence>
      </motion.div>
    </AnimateSharedLayout>
  );
};

function Reason({ handleOnSelect, setAnimate }) {
  const merchantConfig = useSelector(getMerchantConfig);
  const rmsConfig = useSelector(getRmsConfig);
  const selectedOrder = useSelector(getSelectedOrder);
  const selectedProduct = useSelector(getSelectedProduct);
  const selectedGroupProducts = useSelector(getSelectedGroupProducts);
  const preReason = useSelector(getSelectedReason);
  const preSubReason = useSelector(getSelectedSubReason);
  const returnedProducts = useSelector(getReturnedProducts);
  const productid = useSelector(getProductid);
  const productType = useSelector(getProductType);
  const proceed = useSelector(getProceedBool);
  const preUploads = useSelector(getUploads);
  const prePreviews = useSelector(getPreviews);
  const isReplacement = useSelector(getReplacementMode);

  const [selectedReason, setSelectedReason] = useState(preReason);
  const [selectedSubReason, setSelectedSubReason] = useState(preSubReason);
  const [preview, setPreview] = useState(prePreviews);
  const [uploads, setUploads] = useState(preUploads);
  const [quantityInc, setQuantityInc] = useState(selectedProduct.remaining_qty);
  const [reasons, setReasons] = useState([]);
  const [uploadError, setUploadError] = useState(null);
  const [reasonLoading, setReasonLoading] = useState(true);
  const [requiredProduct, setRequiredProduct] = useState(null);
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [verificationLoader, setVerificationLoader] = useState(false);
  const [reasonApiError, setReasonApiError] = useState(false);
  const [exchangeRequired, setExchangeRequired] = useState(false);
  const [otherReason, setOtherReason] = useState(null);
  const [otherReasonError, setOtherReasonError] = useState(false);
  const preUploadMediaIds = useSelector(getUploadMediaIds);
  const [uploadMediaIds, setUploadMediaIds] = useState(preUploadMediaIds);
  const [openFilter, setOpenFilter] = useState(false);
  const [imageUploadCount, setImageUploadCount] = useState(0);
  const [videoUploadCount, setVideoUploadCount] = useState(0);
  const [uploadSuccessMessage, setUploadSuccessMessage] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploading, setUploading] = useState(false);
  const configuredMedia = Array.isArray(selectedReason?.media_configs)
    ? selectedReason.media_configs
    : [];
  const requiresMedia = reasonRequiresMedia(selectedReason);
  const minimumPhotos = getReasonMinimumMediaUploads(selectedReason, "image");
  const minimumVideos = getReasonMinimumMediaUploads(selectedReason, "video");
  const configuredRequirementsMet = areReturnMediaRequirementsMet(
    configuredMedia,
    uploads,
  );
  const mediaRequirementsMet =
    !requiresMedia ||
    (configuredRequirementsMet &&
      uploads.length >=
        getReasonMinimumConfiguredMediaUploads(selectedReason) &&
      imageUploadCount >= minimumPhotos &&
      videoUploadCount >= minimumVideos);

  const dispatch = useDispatch();

  const isExchangeFlow =
    productid !== null &&
    productid !== undefined &&
    productid.indexOf("exchange") !== -1;
  const reasonSubject = isReplacement
    ? "replacement"
    : isExchangeFlow
      ? "exchange"
      : "return";
  const brandPrimaryColor = merchantConfig?.primary_theme_color || "#3B82F6";
  const exchangeOptionSelectedBackground = "rgba(59, 130, 246, 0.08)";
  const proceedLabel = verificationLoader ? (
    <BeatLoader color={merchantConfig.secondary_theme_color} />
  ) : (
    <span>Proceed</span>
  );

  useEffect(() => {
    getReasons(
      productid !== null && productid.indexOf("return") !== -1
        ? "refund"
        : "exchange",
      selectedProduct.product_id,
    )
      .then((resp) => {
        const apiReasons = resp?.data?.reasons || [];
        const shouldShuffleReasons = Boolean(rmsConfig?.randomised_reason_order);
        setReasons(
          shouldShuffleReasons ? shuffleArray(apiReasons) : apiReasons,
        );
        setReasonLoading(false);
      })
      .catch((err) => {
        setReasonApiError(true);
      });
  }, []);

  useEffect(() => {
    setUploadMediaIds(preUploadMediaIds);
  }, [preUploadMediaIds]);

  useEffect(() => {
    dispatch(addUploadMediaIds(uploadMediaIds));
  }, [uploadMediaIds]);

  useEffect(() => {
    if (isReplacement) {
      dispatch(addProductType("same"));
      setExchangeRequired(false);
    }
  }, [dispatch, isReplacement]);

  const handleReason = (reason, openBox, setOpenbox) => {
    reason.sub_reasons.length > 0
      ? setOpenbox(!openBox)
      : setSelectedReason(reason);
    dispatch(addSelectedReason(reason));
  };

  const handleSubReason = (reason, subreason) => {
    setSelectedReason(reason);
    setSelectedSubReason(subreason);
    dispatch(addSelectedReason(reason));
    dispatch(addSelectedSubReason(subreason));
  };

  const dispatchReturnedProducts = () => {
    let tempProducts = [...returnedProducts];
    let line_item_ids = tempProducts.map((a) => a.line_item_id);

    if (selectedGroupProducts.length > 0) {
      for (let i = selectedGroupProducts.length - 1; i >= 0; i--) {
        const product = selectedGroupProducts[i];

        const index = line_item_ids.indexOf(product.line_item_id);

        if (index !== -1) {
          tempProducts.splice(index, 1);
          line_item_ids.splice(index, 1);
        }
      }
    } else {
      if (line_item_ids.indexOf(selectedProduct.line_item_id) !== -1) {
        let index = line_item_ids.indexOf(selectedProduct.line_item_id);
        tempProducts.splice(index, 1);
      }
    }

    let childProducts = selectedGroupProducts.filter(
      (groupProduct) =>
        groupProduct.is_child === true &&
        groupProduct.refund_eligible === true &&
        groupProduct.auto_add === true,
    );

    setUploads([]);
    dispatch(addUploads([]));
    dispatch(
      addReturnedProducts([
        ...tempProducts,
        {
          merchant_id: merchantConfig.merchant_id,
          line_item_id: selectedProduct.line_item_id,

          product_id: selectedProduct.product_id,
          variant_id: selectedProduct.variant_id,
          product_price: selectedProduct.product_price,
          quantity: quantityInc,
          product_title: selectedProduct.product_title,
          variant_name: selectedProduct.variant_name,
          product_sku: selectedProduct.sku,
          original_product_price: selectedProduct.original_product_price,
          variant_weight: selectedProduct?.variant_weight || "",
          variant_weight_unit: selectedProduct?.variant_weight_unit || "",
          variant_image_url: selectedProduct.image_url,

          return_type: "refund",

          customer_reason_code: selectedReason.reason_code,
          customer_reason_label: selectedReason.reason_label,
          customer_reason_comment: otherReason,
          customer_sub_reason_code:
            selectedSubReason !== undefined && selectedSubReason !== null
              ? selectedSubReason.reason_code
              : null,
          customer_sub_reason_label:
            selectedSubReason !== undefined && selectedSubReason !== null
              ? selectedSubReason.reason_label
              : null,

          total_item_discount: selectedProduct.total_item_discount,
          discounted_price: selectedProduct.discounted_price,

          exchange_applicable: selectedProduct.exchange_applicable,
          refund_applicable: selectedProduct.refund_applicable,
          shipment_applicable: selectedProduct.shipment_applicable,

          product_group_id: selectedProduct.product_group_id,
          is_parent: selectedProduct.is_parent,
          is_child: selectedProduct.is_child,
          properties: selectedProduct.properties,

          remaining_qty: selectedProduct.quantity - quantityInc,
          applicable_refund_modes: selectedProduct.applicable_refund_modes,
          ...getLineItemMetadata(selectedProduct),
        },
        ...childProducts.map((childProduct) => ({
          merchant_id: merchantConfig.merchant_id,
          line_item_id: childProduct.line_item_id,

          product_id: childProduct.product_id,
          variant_id: childProduct.variant_id,
          product_price: childProduct.product_price,
          quantity: quantityInc,
          product_title: childProduct.product_title,
          variant_name: childProduct.variant_name,
          product_sku: childProduct.sku,
          original_product_price: childProduct.original_product_price,
          variant_weight: childProduct?.variant_weight || "",
          variant_weight_unit: childProduct?.variant_weight_unit || "",
          variant_image_url: childProduct.image_url,

          return_type: "refund",

          customer_reason_code: selectedReason.reason_code,
          customer_reason_label: selectedReason.reason_label,
          customer_reason_comment: otherReason,
          customer_sub_reason_code:
            selectedSubReason !== undefined && selectedSubReason !== null
              ? selectedSubReason.reason_code
              : null,
          customer_sub_reason_label:
            selectedSubReason !== undefined && selectedSubReason !== null
              ? selectedSubReason.reason_label
              : null,

          total_item_discount: childProduct.total_item_discount,
          discounted_price: childProduct.discounted_price,

          exchange_applicable: childProduct.exchange_applicable,
          refund_applicable: childProduct.refund_applicable,
          shipment_applicable: childProduct.shipment_applicable,

          product_group_id: childProduct.product_group_id,
          is_parent: childProduct.is_parent,
          is_child: childProduct.is_child,
          properties: childProduct.properties,

          remaining_qty: childProduct.quantity - quantityInc,
          applicable_refund_modes: selectedProduct.applicable_refund_modes,
          ...getLineItemMetadata(childProduct),
        })),
      ]),
    );
    dispatch(addProceedBool(true));
    dispatch(updateWebSelected(false));
    dispatch(mobSelected(false));
    dispatch(setReplacementMode(false));
    setVerificationLoader(false);
    setExchangeRequired(false);

    setAnimate(true);
    // Reset animation after it's done if needed
    setTimeout(() => {
      setAnimate(false);
    }, 700); // Duration matches the animation
  };

  const isOtherReason = (reason) =>
    reason?.reason_code === "other" ||
    (reason?.reason_code || "").toLowerCase().includes("other") ||
    (reason.collect_customer_comment && reason.mandate_customer_comment);

  const isValidUploads = () =>
    uploads.length &&
    merchantConfig?.merchant_id &&
    hasReturnMediaReasonIdentity(selectedReason) &&
    selectedOrder?.order_id;

  const handleMediaUpload = async (files) => {
    return getS3SignedUrls(
      files,
      selectedOrder.order_id,
      selectedOrder.order_number,
      selectedProduct?.line_item_id
    );
  };

  const logUploadError = (err, formData, type) => {
    const files = Array.from(formData?.getAll("files") || []).map((f) => ({
      name: f.name,
      type: f.type,
      size: f.size,
    }));
    const ua = navigator.userAgent || "";
    const is_webview = /(WebView|wv)/i.test(ua);
    let webview_version = null;
    const match = ua.match(/WebView\/(\d+(?:\.\d+)*)/i);
    if (match) {
      webview_version = match[1];
    }
    try {
      const payload = {
        err,
        type,
        reason: selectedReason,
        mid: merchantConfig.merchant_id,
        files,
        formData,
        userAgent: navigator.userAgent,
        is_webview,
      };
      if (webview_version) {
        payload.webview_version = webview_version;
      }
      sendWebhook(payload);
    } catch (_) {}
  };

  const compressFile = (file) => {
    return new Promise((resolve) => {
      if (file.type.startsWith("image/")) {
        new Compressor(file, {
          quality: 0.6,
          convertSize: 1000000,
          success(result) {
            const compressed = new File([result], file.name, {
              type: result.type,
            });
            compressed.media_config_id = file.media_config_id;
            compressed.capture_code = file.capture_code;
            compressed.capture_label = file.capture_label;
            resolve(compressed);
          },
          error() {
            resolve(file);
          },
        });
      } else {
        resolve(file);
      }
    });
  };

  async function uploadToS3(file, postData, onProgress) {
    const formData = new FormData();
    Object.entries(postData.fields).forEach(([k, v]) => formData.append(k, v));
    formData.append("file", file);

    const response = await fetch(postData.url, {
      method: "POST",
      body: formData,
    });
    if (!response.ok) {
      const text = await response.text();
      throw new Error(`Upload failed: ${response.status} ${text}`);
    }
    if (onProgress) onProgress();
    return true;
  }

  const updateUploads = (lineItemId, newUploads, onSuccess) => {
    setUploadMediaIds((prev) => {
      // 1️⃣ compute the next state
      const i = prev.findIndex((u) => u.line_item_id === lineItemId);
      const next =
        i !== -1
          ? prev.map((u, idx) =>
              idx === i
                ? { ...u, uploads: [...new Set([...u.uploads, ...newUploads])] }
                : u,
            )
          : [...prev, { line_item_id: lineItemId, uploads: [...newUploads] }];

      // 2️⃣ now that `next` is final, dispatch it synchronously
      onSuccess(next);

      return next;
    });
  };

  const processProceed = async (onSuccess, type) => {
    setOtherReasonError(false);
    setUploadError(null);

    try {
      if (requiresMedia) {
        if (!isValidUploads()) {
          toast.error("Please retry uploading images");
          return;
        }

        const matchingLineItem = uploadMediaIds.find(
          (item) => item.line_item_id === selectedProduct.line_item_id,
        );

        let newIndices = [];

        if (!matchingLineItem) {
          // If the line_item_id is new → all files are non-matching
          newIndices = uploads.map((_, index) => index);
        } else {
          // If it exists, collect the uploaded names
          const uploadedNames = new Set(
            matchingLineItem.uploads.map((u) => u.name),
          );

          // Compare each upload name against the uploadedNames set
          newIndices = uploads.reduce((acc, file, index) => {
            if (!uploadedNames.has(file.name)) {
              acc.push(index);
            }
            return acc;
          }, []);
        }

        if (newIndices.length > 0) {
          const filesToUpload = newIndices.map((i) => uploads[i]);

          const compressedFiles = await Promise.all(
            filesToUpload.map((f) => compressFile(f)),
          );

          const signedUploads = compressedFiles.map((file, index) => ({
            file,
            requestName: `${index}_${sanitizeUploadFilename(file.name)}`,
          }));

          const filesMapping = buildReturnMediaPresignFiles(
            signedUploads,
            selectedProduct.line_item_id,
          );

          const posts = await handleMediaUpload(filesMapping);

          // Use a reducer instead of Object.fromEntries for older mobile browsers.
          const postMap = posts.data.posts.reduce((acc, post) => {
            acc[post.name] = post.post_data;
            return acc;
          }, {});

          try {
            setUploading(true);
            setUploadProgress(0);
            let completed = 0;
            const total = compressedFiles.length;

            const uploadPromises = signedUploads.map(
              ({ file, requestName }) => {
                const postData = postMap[requestName];
                if (!postData) {
                  throw new Error(`No presigned URL for ${file.name}`);
                }
                return uploadToS3(file, postData, () => {
                  completed += 1;
                  setUploadProgress(Math.round((completed / total) * 100));
                });
              },
            );
            await Promise.all(uploadPromises);
            setUploading(false);
            setUploadProgress(100);

            const currentUploadIds = posts.data.posts.map((post, index) => ({
              media: post.upload_media_id,
              name: signedUploads[index]?.file?.name || post.name,
              type: post.content_type,
              media_config_id: signedUploads[index]?.file?.media_config_id,
              capture_code: signedUploads[index]?.file?.capture_code,
              capture_label: signedUploads[index]?.file?.capture_label,
            }));

            updateUploads(
              selectedProduct.line_item_id,
              currentUploadIds,
              onSuccess,
            );
          } catch (e) {
            setUploading(false);
            throw e;
          }
        } else {
          onSuccess();
        }
      } else {
        dispatch(setImageUploads([]));
        onSuccess();
      }
    } catch (err) {
      console.log(err);
      const errorMessage = getApiErrorMessage(
        err,
        "Please try re-uploading the image",
      );

      setUploadError(errorMessage);

      setUploadSuccessMessage(null);
      toast.error(getApiErrorMessage(err, "Error uploading media files"));
      logUploadError(err, {}, type);
    } finally {
      setVerificationLoader(false);
      setUploading(false);
    }
  };

  const handleProceed = async (return_type) => {
    if (!productType && return_type === "exchange" && !isReplacement) {
      toast.error("Please select exchange preference");
      setVerificationLoader(false);
      setExchangeRequired(true);
      return;
    }

    if (isOtherReason(selectedReason)) {
      if (!otherReason) {
        toast.error("Please mention the reason in the comments", {
          position: toast.POSITION.TOP_RIGHT,
          autoClose: 6000,
          hideProgressBar: false,
          closeOnClick: true,
        });
        setVerificationLoader(false);
        setOtherReasonError(true);
        return;
      }
    }

    const onSuccess = () => {
      if (return_type === "exchange") {
        setUploadSuccessMessage(null);
        dispatch(addProceedBool(true));
      } else {
        setUploadSuccessMessage(null);
        setTimeout(() => {
          dispatchReturnedProducts();
        });
      }
    };

    try {
      await processProceed(onSuccess, `${return_type} proceed button`);
    } finally {
      setVerificationLoader(false);
    }
  };

  return (
    <StyledReasonWrapper
      primary_color={merchantConfig.primary_theme_color}
      selectedReason={selectedReason !== null && selectedReason.reason_label}
      openFilter={openFilter}
      rounded={merchantConfig.use_rounded_edge_buttons}
    >
      <div
        className="reason-panel-header"
        style={{
          padding: "15px 20px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.08)",
          position: "sticky",
          top: "0",
          background: "#fff",
          zIndex: "99",
        }}
      >
        <div style={{ display: "flex", alignItems: "center" }}>
          {((requiredProduct === null && productType !== null && proceed) ||
            requiredProduct !== null) && (
            <ArrowBackIcon
              style={{
                marginRight: "6px",
                cursor: "pointer",
                width: "20px",
                height: "20px",
              }}
              onClick={() => {
                if (requiredProduct === null) {
                  dispatch(addProceedBool(null));
                  dispatch(addProductType(null));
                } else {
                  setRequiredProduct(null);
                  setSelectedVariant(null);
                }
              }}
            />
          )}
          <LazyLoadImage
            effect="opacity"
            src={selectedProduct.image_url}
            alt="Preview"
            style={{
              height: "35px",
              width: "35px",
              borderRadius: merchantConfig.use_rounded_edge_buttons
                ? "10px"
                : "0px",
              display: "block",
            }}
          />
          <div style={{ marginLeft: "10px" }}>
            <p className="product_title">{selectedProduct.product_title}</p>
          </div>
        </div>

        <p
          style={{ cursor: "pointer" }}
          onClick={() => {
            dispatch(updateWebSelected(false));
            dispatch(mobSelected(false));
            dispatch(setReplacementMode(false));
          }}
        >
          {" "}
          <CloseIcon
            style={{ width: "20px", height: "20px", stroke: "#3B444D" }}
          />{" "}
        </p>
      </div>

      <Divider />

      <div className="reason-scroll-content">
      {proceed && productType === "same" ? (
        <SameProduct
          quantityInc={quantityInc}
          productType={productType}
          selectedVariant={selectedVariant}
          setSelectedVariant={setSelectedVariant}
          otherReason={otherReason}
          setAnimate={setAnimate}
          setUploads={setUploads}
        />
      ) : proceed && productType === "different" ? (
        <DifferentProduct
          quantityInc={quantityInc}
          productType={productType}
          requiredProduct={requiredProduct}
          setRequiredProduct={setRequiredProduct}
          selectedVariant={selectedVariant}
          setSelectedVariant={setSelectedVariant}
          otherReason={otherReason}
          setAnimate={setAnimate}
          openFilter={openFilter}
          setOpenFilter={setOpenFilter}
          setUploads={setUploads}
        />
      ) : (
        <Fragment>
          <div className="reason-form">
            <p className="choose-reason-statement">
              <span>Please choose your reason for </span>
              <span>{reasonSubject}</span>
              <span> below.</span>
            </p>

            {reasonApiError ? (
              <LoadingError
                title="Something went wrong"
                message="We couldn't load the exchange reasons."
                marginTop="100px"
              />
            ) : reasonLoading && selectedReason == null ? (
              <div>
                {[...Array(6).keys()].map((a) => {
                  return (
                    <div
                      style={{
                        border: "1px solid  rgba(17, 17, 17, 0.2)",
                        padding: "16px",
                        marginTop: "20px",
                        borderRadius: "6px",
                      }}
                      key={a}
                    >
                      <Skeleton type="text" style={{ width: "100%" }} />
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="reasons">
                <AnimateSharedLayout>
                  <motion.div layout>
                    {reasons.length > 0 &&
                      reasons.map((reason, i) => {
                        return (
                          <Reasonbox
                            reason={reason}
                            key={i}
                            merchantConfig={merchantConfig}
                            selectedReason={selectedReason}
                            handleReason={handleReason}
                            handleSubReason={handleSubReason}
                          />
                        );
                      })}
                  </motion.div>
                </AnimateSharedLayout>
              </div>
            )}
          </div>

          {selectedReason && selectedReason !== null && (
            <div style={{ padding: "14px 20px" }}>
              <div
                style={{
                  padding: "10px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  borderRadius: merchantConfig.use_rounded_edge_buttons
                    ? "6px"
                    : "0px",
                  background: merchantConfig.merchant_id === 977 ? "#F9FAFC" : `${merchantConfig.primary_theme_color}15`,
                  border: merchantConfig.merchant_id === 977 ? "1px solid #E3E6EE" : "none",
                }}
              >
                <div>
                  <p style={{ color: "#3B444D", fontSize: "16px" }}>
                    {selectedReason.reason_label}
                  </p>
                  {selectedSubReason && selectedSubReason !== null && (
                    <p
                      style={{
                        marginTop: "6px",
                        color: "#3B444D",
                        fontSize: "14px",
                      }}
                    >
                      {selectedSubReason.reason_label}
                    </p>
                  )}
                </div>
                <p
                  style={{ color: "3B444D", fontSize: "16px", cursor: "pointer" }}
                  onClick={() => {
                    setSelectedSubReason(null);
                    setSelectedReason(null);
                  }}
                >
                  {" "}
                  <CloseIcon
                    style={{
                      width: "18px",
                      height: "18px",
                      stroke: "#3B444D",
                      marginTop: "2px",
                    }}
                  />{" "}
                </p>
              </div>

              {(selectedReason.reason_code === "other" ||
                (selectedReason.reason_code || "")
                  .toLowerCase()
                  .includes("other") ||
                selectedReason.collect_customer_comment) && (
                <div>
                  <p
                    style={{
                      color: "#3B444D",
                      fontSize: "14px",
                      marginTop: "10px",
                      fontWeight: "500",
                    }}
                  >
                    Comments
                  </p>
                  <TextareaAutosize
                    placeholder="Mention your reason here"
                    value={otherReason || ""}
                    onChange={(e) => {
                      setOtherReason(e.target.value);
                      setOtherReasonError(false);
                    }}
                    style={{
                      width: "100%",
                      marginTop: "10px",
                      height: "70px",
                      padding: "10px",
                      fontSize: "14px",
                      fontFamily:
                        merchantConfig &&
                        merchantConfig.google_font_family !== null
                          ? merchantConfig.google_font_family
                          : "Inter",
                      border: otherReasonError
                        ? "1px solid #e74c3c"
                        : "1px solid #3B444D",
                    }}
                  />
                </div>
              )}

              {selectedReason.comment !== null &&
                selectedReason.comment !== "" && (
                  <p
                    style={{
                      color: "#3B444D",
                      fontSize: "12px",
                      marginTop: "10px",
                    }}
                  >
                    <span
                      style={{
                        background: "#DD435D33",
                        color: "#DD435D99",
                        marginRight: "5px",
                        padding: "4px",
                        fontWeight: "500",
                        borderRadius: "2px",
                      }}
                    >
                      <span>Note :</span>
                    </span>
                    <span>{selectedReason.comment}</span>
                  </p>
                )}

              <Quantity
                quantityInc={quantityInc}
                setQuantityInc={setQuantityInc}
                originalQuantity={selectedProduct.remaining_qty}
              />

              {requiresMedia && (
                <ImageUpload
                  selectedReason={selectedReason}
                  uploads={uploads}
                  setUploads={setUploads}
                  preview={preview}
                  setPreview={setPreview}
                  uploadMediaIds={uploadMediaIds}
                  setUploadMediaIds={setUploadMediaIds}
                  setImageUploadCount={setImageUploadCount}
                  setVideoUploadCount={setVideoUploadCount}
                  imageUploadCount={imageUploadCount}
                  videoUploadCount={videoUploadCount}
                  lineItemId={selectedProduct.line_item_id}
                />
              )}

              {uploading && (
                <div style={{ marginTop: "10px" }}>
                  <div
                    style={{
                      width: "100%",
                      background: "#e0e0e0",
                      height: "6px",
                      borderRadius: "4px",
                    }}
                  >
                    <div
                      style={{
                        width: `${uploadProgress}%`,
                        background: merchantConfig.primary_theme_color,
                        height: "100%",
                        borderRadius: "4px",
                        transition: "width 0.3s ease",
                      }}
                    />
                  </div>
                  <p
                    style={{
                      fontSize: "12px",
                      marginTop: "4px",
                      color: "#3B444D",
                    }}
                  >
                    {uploadProgress}% uploaded
                  </p>
                </div>
              )}

              {uploadSuccessMessage !== null && (
                <p style={{ color: "green" }}>{uploadSuccessMessage}</p>
              )}

              {uploadError !== null && (
                <p style={{ color: "red" }}>{uploadError}</p>
              )}

              <div>
                {isExchangeFlow && !isReplacement && (
                  <Fragment>
                    <p
                      className="preference"
                      style={{
                        fontWeight: "700",
                        fontSize: "16px",
                        color: !exchangeRequired ? `#3B444D` : "#ff0000",
                      }}
                    >
                      Select type of Exchange
                    </p>
                    <FormControl>
                      <RadioGroup
                        row
                        aria-labelledby="product-type-options"
                        name="product-type-options"
                        sx={{
                          gap: "12px",
                          marginTop: "6px"
                        }}
                      >
                        {(selectedOrder.exchange_configurations.length === 0 ||
                          selectedOrder.exchange_configurations.includes(
                            "same_variant_exchange",
                          )) && (
                          <FormControlLabel
                            id="same"
                            checked={productType === "same"}
                            value="same"
                            sx={{
                              margin: 0,
                              ".MuiFormControlLabel-label": {
                                margin: 0,
                              },
                            }}
                            control={
                              <Radio
                                sx={{
                                  position: "absolute",
                                  opacity: 0,
                                  width: 0,
                                  height: 0,
                                  padding: 0,
                                  margin: 0,
                                }}
                                size="small"
                                onChange={() => {
                                  dispatch(addProductType("same"));
                                  setExchangeRequired(false);
                                }}
                              />
                            }
                            label={
                              <p
                                className="radio-label"
                                style={{
                                  fontSize: "16px",
                                  margin: 0,
                                  padding: "10px 18px",
                                  borderRadius: "999px",
                                  border: `1px solid ${productType === "same" ? brandPrimaryColor : "#D0D5DD"}`,
                                  color: productType === "same" ? brandPrimaryColor : "#344054",
                                  background:
                                    productType === "same"
                                      ? exchangeOptionSelectedBackground
                                      : "#FFFFFF",
                                  fontWeight: "500",
                                  lineHeight: 1.2,
                                  cursor: "pointer",
                                  transition:
                                    "border-color 0.2s ease, color 0.2s ease, background-color 0.2s ease",
                                }}
                              >
                                {rmsConfig.same_variant_option_title}
                              </p>
                            }
                          />
                        )}
                        {rmsConfig.exchange_with_any_product &&
                          (selectedOrder.exchange_configurations.length === 0 ||
                            selectedOrder.exchange_configurations.includes(
                              "diff_product_variant",
                            )) && (
                            <FormControlLabel
                              disabled={!rmsConfig.exchange_with_any_product}
                              id="different"
                              checked={productType === "different"}
                              value="different"
                              sx={{
                                margin: 0,
                                ".MuiFormControlLabel-label": {
                                  margin: 0,
                                },
                              }}
                              control={
                                <Radio
                                  size="small"
                                  sx={{
                                    position: "absolute",
                                    opacity: 0,
                                    width: 0,
                                    height: 0,
                                    padding: 0,
                                    margin: 0,
                                  }}
                                  onChange={() => {
                                    dispatch(addProductType("different"));
                                    setExchangeRequired(false);
                                  }}
                                />
                              }
                              label={
                                <p
                                  className="radio-label"
                                  style={{
                                    fontSize: "16px",
                                    margin: 0,
                                    padding: "10px 18px",
                                    borderRadius: "999px",
                                    border: `1px solid ${productType === "different" ? brandPrimaryColor : "#D0D5DD"}`,
                                    color:
                                      productType === "different"
                                        ? brandPrimaryColor
                                        : "#344054",
                                    background:
                                      productType === "different"
                                        ? exchangeOptionSelectedBackground
                                        : "#FFFFFF",
                                    fontWeight: "500",
                                    lineHeight: 1.2,
                                    cursor: "pointer",
                                    transition:
                                      "border-color 0.2s ease, color 0.2s ease, background-color 0.2s ease",
                                  }}
                                >
                                  {rmsConfig.other_product_option_title}
                                </p>
                              }
                            />
                          )}
                      </RadioGroup>
                    </FormControl>
                  </Fragment>
                )}
              </div>
            </div>
          )}
        </Fragment>
      )}
      </div>

      {selectedReason &&
        !(proceed && (productType === "same" || productType === "different")) && (
          <Button
            variant="contained"
            className="proceed-btn"
            onClick={async () => {
              setVerificationLoader(true);
              await handleProceed(isExchangeFlow ? "exchange" : "refund");
            }}
            style={{
              borderRadius: merchantConfig.use_rounded_edge_buttons
                ? "0px 0px 10px 10px"
                : "0px",
              fontSize: "16px",
            }}
            disabled={verificationLoader || !mediaRequirementsMet}
            endIcon={
              verificationLoader ? null : (
                <ArrowForwardIcon sx={{ position: "relative", top: "-1px" }} />
              )
            }
          >
            <span>{proceedLabel}</span>
          </Button>
        )}
    </StyledReasonWrapper>
  );
}

export default Reason;
